import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Activity,
  FileEdit,
  FilePlus,
  Trash2,
  Camera,
  RotateCcw,
  GitBranch,
  ListTree,
  Settings2,
  Globe,
  Image as ImageIcon,
  UserPlus,
  UserMinus,
  UserCheck,
  ClipboardCheck,
  RefreshCw,
} from "lucide-react";

const ACTION_META = {
  "chapter.created": { icon: FilePlus, label: "created a chapter" },
  "chapter.updated": { icon: FileEdit, label: "edited a chapter" },
  "chapter.deleted": { icon: Trash2, label: "deleted a chapter" },
  "chapter.snapshot": { icon: Camera, label: "saved a snapshot" },
  "chapter.restored": { icon: RotateCcw, label: "restored a chapter version" },
  "chapter.version_deleted": { icon: Trash2, label: "deleted a chapter version" },
  "outline.updated": { icon: ListTree, label: "updated the outline" },
  "outline.generated": { icon: GitBranch, label: "generated chapters from outline" },
  "book.settings_updated": { icon: Settings2, label: "updated book settings" },
  "book.published": { icon: Globe, label: "made the book public" },
  "book.unpublished": { icon: Globe, label: "made the book private" },
  "book.cover_uploaded": { icon: ImageIcon, label: "updated the cover" },
  "collaborator.invited": { icon: UserPlus, label: "invited a collaborator" },
  "collaborator.removed": { icon: UserMinus, label: "removed a collaborator" },
  "collaborator.joined": { icon: UserCheck, label: "joined as a collaborator" },
  "review.run": { icon: ClipboardCheck, label: "ran a pre-publish review" },
};

function formatTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

function describeMeta(action, meta) {
  if (!meta) return "";
  if (action === "chapter.updated") {
    const fields = (meta.fields || []).filter((f) => f !== "updated_at");
    return fields.length ? `Updated ${fields.join(", ")}` : "";
  }
  if (action === "chapter.snapshot") return meta.label && meta.label !== "(unlabelled)" ? `Label: ${meta.label}` : "";
  if (action === "chapter.created" || action === "chapter.deleted") return meta.title || "";
  if (action === "outline.updated") return `${meta.chapters ?? 0} chapter${meta.chapters === 1 ? "" : "s"}`;
  if (action === "outline.generated") return `${meta.chapters_created ?? 0} chapter${meta.chapters_created === 1 ? "" : "s"} created`;
  if (action === "book.settings_updated") {
    const fields = meta.fields || [];
    return fields.length ? `Fields: ${fields.join(", ")}` : "";
  }
  if (action === "collaborator.invited" || action === "collaborator.removed") {
    return meta.invitee_email ? `${meta.invitee_email}${meta.role ? ` · ${meta.role}` : ""}` : "";
  }
  if (action === "collaborator.joined") return meta.role ? `Role: ${meta.role}` : "";
  if (action === "review.run") return meta.overall_score != null ? `Overall score: ${meta.overall_score}/10` : "";
  return "";
}

export default function ActivityFeed({ bookId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/books/${bookId}/activity`);
      setEvents(data || []);
    } catch {
      toast.error("Failed to load activity");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId]);

  return (
    <div className="max-w-3xl" data-testid="activity-feed">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="label-eyebrow mb-2">Co-author activity</div>
          <h2 className="font-serif text-3xl">Activity feed</h2>
          <p className="text-sm mt-1.5" style={{ color: "var(--bf-text-muted)" }}>
            Every action — edits, snapshots, version restores, outline updates, publishing — recorded here.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="activity-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {loading ? (
        <p className="text-sm py-6" style={{ color: "var(--bf-text-muted)" }} data-testid="activity-loading">Loading…</p>
      ) : events.length === 0 ? (
        <div
          className="p-10 text-center rounded-lg"
          style={{ background: "var(--bf-surface)", border: "1px dashed var(--bf-border)" }}
          data-testid="activity-empty"
        >
          <Activity className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--bf-accent)" }} />
          <h3 className="font-serif text-xl mb-1">Nothing here yet</h3>
          <p className="text-sm" style={{ color: "var(--bf-text-muted)" }}>
            As you and your collaborators edit, snapshots and outline changes will appear here.
          </p>
        </div>
      ) : (
        <ul className="space-y-2.5" data-testid="activity-list">
          {events.map((e) => {
            const meta = ACTION_META[e.action] || { icon: Activity, label: e.action };
            const Icon = meta.icon;
            const detail = describeMeta(e.action, e.meta);
            return (
              <li
                key={e.event_id}
                className="flex gap-3 p-4 rounded-lg"
                style={{ background: "white", border: "1px solid var(--bf-border)" }}
                data-testid={`activity-item-${e.event_id}`}
              >
                <div
                  className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                  style={{ background: "var(--bf-surface)", color: "var(--bf-primary)" }}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm" data-testid={`activity-action-${e.event_id}`}>
                    <strong>{e.actor_name || e.actor_email || "Someone"}</strong>{" "}
                    <span style={{ color: "var(--bf-text-muted)" }}>{meta.label}</span>
                  </p>
                  {detail && (
                    <p className="text-xs mt-0.5 truncate" style={{ color: "var(--bf-text-muted)" }} title={detail}>
                      {detail}
                    </p>
                  )}
                  <p className="text-xs mt-1" style={{ color: "var(--bf-text-muted)" }}>
                    {formatTime(e.created_at)}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
